import { useCallback, useMemo } from 'react';
import { CampaignActions } from '@copilot/common/store/actions/campaign';
import { useSelector } from 'react-redux';
import {
    ScheduleHierarchyType,
    SYSTEM_DEFAULT_SCHEDULE,
} from '@copilot/common/pages/settings/schedule/constant';
import { CampaignSelectors } from '@copilot/common/store/selectors/campaign';
import { CampaignType } from '@copilot/data/responses/interface';
import notificationManager from '@copilot/common/utils/notificationManager';
import { useNavigate } from 'react-router-dom';
import { ScheduleUtility } from '@copilot/common/utils/schedule';
import { useFetch } from '../common';
import { CampaignManager, LinkedInManager } from '../../../../core/data/managers';
import { CampaignStatusEnum } from '@copilot/data/requests/models';
import { withNotification } from '@copilot/common/hoc/utils';
import { NotificationContentType } from '@copilot/common/constant/notificationContent';
import { OrganizationMemberSelectors } from '@copilot/common/store/selectors/organizationMember';
import { OrganizationType } from '@copilot/common/store/models/const/enum';
import { Campaign, CampaignStatus, CampaignStatuses } from './campaignTypes';
import { useFeatureFlags } from '../useFeatureFlags/useFeatureFlags';

function campaignStatusEnumToConst(status: CampaignStatusEnum): CampaignStatus {
    switch (status) {
        case CampaignStatusEnum.Enabled:
            return CampaignStatuses.Enabled;
        case CampaignStatusEnum.Disabled:
            return CampaignStatuses.Disabled;
        default:
            return CampaignStatuses.Unknown;
    }
}

/**
 * Fetches campaigns
 */
export function useCampaigns(): ReadonlyArray<Campaign> {
    const campaigns = useSelector(CampaignSelectors.getCampaigns);
    return campaigns.map((campaign) => ({
        id: campaign.id,
        name: campaign.name,
        type: campaign.type,
        status: campaignStatusEnumToConst(campaign.status),
    }));
}

export function useCreateCampaign() {
    const { isCampaignSetupV2Enabled } = useFeatureFlags();
    const [, createCampaign] = useFetch(
        CampaignManager.createCampaign,
        CampaignActions.loadCampaigns,
        (r) => [r]
    );

    const navigate = useNavigate();

    return useCallback(
        async (name: string, type: CampaignType) => {
            try {
                const campaign = await createCampaign(name, type);
                notificationManager.showSuccessNotification({
                    message: 'Campaign created',
                });
                try {
                    await CampaignManager.createSchedule(campaign.id, {
                        ...SYSTEM_DEFAULT_SCHEDULE,
                        timezoneCode: ScheduleUtility.getLocaleTimezone(),
                        ownerId: campaign.id,
                        hierarchyType: ScheduleHierarchyType.Campaign,
                    });
                } catch {
                    console.warn('Failed to create schedule when creating campaign');
                }
                navigate(
                    isCampaignSetupV2Enabled
                        ? `/campaigns/${campaign.id}/configure`
                        : `/campaign/${campaign.id}`
                );
            } catch {
                notificationManager.showErrorNotification({
                    message: 'Failed to create campaign',
                    description: 'Please refresh the page and try again',
                });
            }
        },
        [createCampaign]
    );
}

/**
 * Terminate Automation on connections in campaign
 * @param campaignId campaignId of connections
 * @param notificationContent notification content to display after termination
 * @param campaignMemberId campaignMemberId connections belong to
 */
export function useTerminateAutomation(
    campaignId: string,
    notificationContent: NotificationContentType,
    campaignMemberId?: string
) {
    const isAdmin = !!useSelector(OrganizationMemberSelectors.getAdminMember);
    const activeMember = useSelector(OrganizationMemberSelectors.getActiveMember);
    const orgMemberId = activeMember?.id;
    const isTeamsOrgAdmin =
        activeMember?.orgType === OrganizationType.Enterprise && activeMember.isOrgAdmin;
    const omid = useMemo(
        () => (isAdmin && orgMemberId ? orgMemberId : null),
        [isAdmin, orgMemberId]
    );

    const terminateConnectionWithNotification = withNotification(
        LinkedInManager.terminateMessageAutomationByConnections,
        notificationContent.Success,
        notificationContent.Fail
    );

    const adminTerminateConnectionWithNotification = withNotification(
        LinkedInManager.terminateMessageAutomationByAdmin,
        notificationContent.Success,
        notificationContent.Fail
    );

    const fetchTerminate = async (connectionsIds: string[]) => {
        if (isAdmin || isTeamsOrgAdmin) {
            await adminTerminateConnectionWithNotification(campaignId, connectionsIds, omid);
        } else if (campaignMemberId)
            await terminateConnectionWithNotification(
                campaignId,
                campaignMemberId,
                connectionsIds,
                omid
            );
    };

    return fetchTerminate;
}
